import React from "react";
import { createFileUploadSlot, deleteReferenceFile, getReferenceFiles, uploadReferenceFile } from "../api/filesClientApi";
import { ThumbnailImageGallery } from "./ThumbnailImageGallery";
import { fileNameHelper, FileRecord, rng } from "../amorg-common";
import { FormField } from "./formField/FormField";
import { FileUploadButton } from "./formField/FileUploadButton";
import { ImageFileExtension, ImageFileExtensions } from '../amorg-common/DTOs/FileExtension';
import { LoadingPageOverlay } from "./LoadingPageOverlay";
import { Flex } from "./compositeComponents/FlexHelpers";


export function FileUploadSlotPanel(props:{
    uploadSlotId?:string,
    onUploadslotCreated?: (uploadSlotId:string) => void
}):JSX.Element
{
    const [uploadSlotId, setUploadSlotId] = React.useState(props.uploadSlotId || '');
    const [files, setFiles] = React.useState<FileRecord[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string>();

    React.useEffect(() => {
        const init = async () => {
            if(uploadSlotId)
            {
                setFiles(await getReferenceFiles({ type:'FileUploadSlot', id: props.uploadSlotId }));
            }
            setLoading(false);
        };
        init();
    }, []);


    async function uploadFile(uploadSlotId:string, file:File):Promise<boolean>
    {
        const fail = (message:string) => {
            setError(message);
            return false;
        }

        const result = await uploadReferenceFile({ type:'FileUploadSlot', id:uploadSlotId }, file);
        if(result.isSuccess)
        {
            await setFiles(await getReferenceFiles({ type:'FileUploadSlot', id: uploadSlotId }));
            setError('');
            return true;
        }
        else
        {
            if(result.errorMessage === 'uploadReferenceFile_empty_file')
                return fail('Filen må ikke være tom');
            else if(result.errorMessage === 'uploadReferenceFile_file_too_big')
                return fail('Filen er for stor. Maksimal størrelse er 10MB');
            else if(result.errorMessage === 'uploadReferenceFile_invalid_file_extension')
                return fail('Ugyldig filtype. Tilladte filtyper er: ' + ImageFileExtensions.join(', '));
            else
                return fail('Der skete en fejl ved upload af filen. Kontakt AMORG support hvis problet fortsætter');
        }
    }

    async function handleFileUpload(file:File)
    {
        setLoading(true);

        if(uploadSlotId)
        {
            await uploadFile(uploadSlotId, file);
            setLoading(false);
        }
        else
        {
            const newUploadSlotId = rng.getRandomId();
            const result = await createFileUploadSlot(newUploadSlotId);
            if(result.isSuccess)
            {
                setUploadSlotId(newUploadSlotId);
                if(await uploadFile(newUploadSlotId, file))
                {
                    props.onUploadslotCreated?.(newUploadSlotId);
                }
            }
            else
            {
                setError('Der skete en uventet fejl. Kontakt AMORG support hvis problet fortsætter');
            }
            setLoading(false);
        }
    }

    async function handleDeleteFile(privateFileName:string)
    {
        setLoading(true);
        await deleteReferenceFile(privateFileName);
        setFiles(await getReferenceFiles({ type:'FileUploadSlot', id: props.uploadSlotId }));
        setLoading(false);
    }

    return (
        <Flex flexDirection="column" gap={8}>
            <LoadingPageOverlay loading={loading} />
            
            <ThumbnailImageGallery files={files.filter(f => fileNameHelper.isImageFile(f.privateFileName))}
                onDelete={handleDeleteFile} />
            <FormField
                label=""
                inputComponent={
                    <FileUploadButton
                        inputName='uploaded_file'
                        fileTypes={ImageFileExtensions.map(e => e as ImageFileExtension)}
                        onClickUpload={handleFileUpload} 
                        text='Upload fil'
                    />
                }
                message={error}
                inErrorState={!!error}
            />
        </Flex>
    );
}
